import { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Container,
    Grid,
    Paper,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Popup from "../../../components/Popup";
import { getSchoolIdForUser } from "../../../utils/userContext";
import { downloadEmisReport, fetchEmisDashboard } from "../../../utils/emisExport";

const y = new Date().getFullYear();
const ACADEMIC_YEARS = [`${y - 1}/${y}`, `${y}/${y + 1}`, `${y + 1}/${y + 2}`];
const TERMS = ["", "Term 1", "Term 2", "Term 3"];

const REPORT_ICONS = {
    "annual-census": DescriptionOutlinedIcon,
    "school-particulars": SchoolOutlinedIcon,
    enrollment: GroupsOutlinedIcon,
    "census-enrollment": GroupsOutlinedIcon,
    attendance: FactCheckOutlinedIcon,
    teachers: GroupsOutlinedIcon,
    performance: AssessmentOutlinedIcon,
    infrastructure: BusinessOutlinedIcon,
};

function StatTile({ label, value, sub }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}>
                {label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5 }}>
                {value ?? "—"}
            </Typography>
            {sub ? (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {sub}
                </Typography>
            ) : null}
        </Paper>
    );
}

const EmisReportsDashboard = () => {
    const { currentUser } = useSelector((s) => s.user);
    const schoolId = getSchoolIdForUser(currentUser);

    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState("");
    const [dashboard, setDashboard] = useState(null);
    const [showPopup, setShowPopup] = useState(false);
    const [message, setMessage] = useState("");

    const [academicYear, setAcademicYear] = useState(ACADEMIC_YEARS[1]);
    const [term, setTerm] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const loadDashboard = useCallback(async () => {
        if (!schoolId) return;
        setLoading(true);
        try {
            const data = await fetchEmisDashboard({
                schoolId,
                academicYear,
                term: term || undefined,
                from: fromDate || undefined,
                to: toDate || undefined,
            });
            setDashboard(data);
        } catch (err) {
            setMessage(err.message || "Could not load EMIS reports");
            setShowPopup(true);
            setDashboard(null);
        } finally {
            setLoading(false);
        }
    }, [schoolId, academicYear, term, fromDate, toDate]);

    useEffect(() => {
        loadDashboard();
    }, [loadDashboard]);

    const reportTypes = dashboard?.reportTypes || [];
    const summary = useMemo(() => dashboard?.summary || {}, [dashboard]);
    const school = dashboard?.school || {};

    const handleExport = async (reportId) => {
        if (!schoolId) return;
        setExporting(reportId);
        try {
            await downloadEmisReport({
                schoolId,
                report: reportId,
                academicYear,
                term: term || undefined,
                from: fromDate || undefined,
                to: toDate || undefined,
            });
        } catch (err) {
            setMessage(err.message || "Export failed");
            setShowPopup(true);
        } finally {
            setExporting("");
        }
    };

    const censusReady = useMemo(() => {
        return Boolean(summary.studentCount && summary.emisCodeSet && summary.teacherCount);
    }, [summary]);

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Stack spacing={3}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        EMIS / DEMIS reporting
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, maxWidth: 720 }}>
                        Generate Ministry of Education returns for your District Education Manager (DEM) and
                        national EMIS (mis.education.gov.mw). Exports use standard census column headers in
                        CSV format ready for upload or manual transfer.
                    </Typography>
                </Box>

                {!summary.emisCodeSet && !loading ? (
                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        Your <strong>MoE school code</strong> is not set. Add it under{" "}
                        <Button component={RouterLink} to="/Admin/profile" size="small">
                            School profile
                        </Button>{" "}
                        so EMIS exports include the official registration number.
                    </Alert>
                ) : null}

                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Academic year"
                                value={academicYear}
                                onChange={(e) => setAcademicYear(e.target.value)}
                                SelectProps={{ native: true }}
                            >
                                {ACADEMIC_YEARS.map((yr) => (
                                    <option key={yr} value={yr}>
                                        {yr}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                select
                                fullWidth
                                size="small"
                                label="Term (performance)"
                                value={term}
                                onChange={(e) => setTerm(e.target.value)}
                                SelectProps={{ native: true }}
                            >
                                {TERMS.map((t) => (
                                    <option key={t || "all"} value={t}>
                                        {t || "All terms"}
                                    </option>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Attendance from"
                                InputLabelProps={{ shrink: true }}
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Attendance to"
                                InputLabelProps={{ shrink: true }}
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : dashboard ? (
                    <>
                        <Paper
                            sx={{
                                p: 2.5,
                                borderRadius: 2,
                                background: (t) =>
                                    `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
                                color: "primary.contrastText",
                            }}
                        >
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                justifyContent="space-between"
                                alignItems={{ xs: "flex-start", sm: "center" }}
                                spacing={2}
                            >
                                <Box>
                                    <Typography variant="overline" sx={{ opacity: 0.9 }}>
                                        Annual School Census
                                    </Typography>
                                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                                        {school.schoolName || "School"} — {academicYear}
                                    </Typography>
                                    <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap", gap: 0.5 }}>
                                        <Chip
                                            size="small"
                                            label={`EMIS: ${school.emisCode || "Not set"}`}
                                            sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "inherit" }}
                                        />
                                        <Chip
                                            size="small"
                                            label={`${school.district || "—"} · ${school.region || "—"}`}
                                            sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "inherit" }}
                                        />
                                        <Chip
                                            size="small"
                                            label={censusReady ? "Ready for export" : "Complete school data"}
                                            color={censusReady ? "success" : "warning"}
                                            variant={censusReady ? "filled" : "outlined"}
                                            sx={censusReady ? {} : { borderColor: "rgba(255,255,255,0.5)", color: "inherit" }}
                                        />
                                    </Stack>
                                </Box>
                                <Button
                                    variant="contained"
                                    color="inherit"
                                    size="large"
                                    startIcon={
                                        exporting === "annual-census" ? (
                                            <CircularProgress size={18} color="inherit" />
                                        ) : (
                                            <FileDownloadOutlinedIcon />
                                        )
                                    }
                                    disabled={Boolean(exporting)}
                                    onClick={() => handleExport("annual-census")}
                                    sx={{ color: "primary.main", fontWeight: 700, whiteSpace: "nowrap" }}
                                >
                                    Download full census (CSV)
                                </Button>
                            </Stack>
                        </Paper>

                        <Grid container spacing={2}>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile label="Students" value={summary.studentCount} sub={`M ${summary.maleStudents} · F ${summary.femaleStudents}`} />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile label="Teachers" value={summary.teacherCount} sub={`Teaching: ${summary.teachingStaff}`} />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile
                                    label="Attendance rate"
                                    value={summary.overallAttendanceRate != null ? `${summary.overallAttendanceRate}%` : "—"}
                                    sub={`${summary.attendanceSessions || 0} sessions`}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile
                                    label="Pupil : classroom"
                                    value={summary.pupilClassroomRatio ?? "—"}
                                    sub={`${summary.classroomCount || 0} rooms · cap ${summary.classroomCapacity || 0}`}
                                />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile label="Special needs" value={summary.specialNeedsCount} />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile label="Classes" value={summary.classCount} />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile label="Facilities" value={summary.facilityCount} sub={`${summary.assetCount || 0} assets`} />
                            </Grid>
                            <Grid item xs={6} sm={4} md={3}>
                                <StatTile label="Assessments" value={summary.assessmentRecords} sub="Exam result rows" />
                            </Grid>
                        </Grid>

                        <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
                                One-click exports for DEM / Ministry
                            </Typography>
                            <Grid container spacing={2}>
                                {reportTypes.map((report) => {
                                    const Icon = REPORT_ICONS[report.id] || DescriptionOutlinedIcon;
                                    const isPrimary = report.id === "annual-census";
                                    return (
                                        <Grid item xs={12} sm={6} md={4} key={report.id}>
                                            <Card
                                                variant="outlined"
                                                sx={{
                                                    borderRadius: 2,
                                                    height: "100%",
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    borderColor: isPrimary ? "primary.main" : undefined,
                                                }}
                                            >
                                                <CardContent sx={{ flex: 1 }}>
                                                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                                                        <Icon color="primary" />
                                                        <Box>
                                                            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                                                {report.label}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                                                {report.description}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                </CardContent>
                                                <CardActions sx={{ px: 2, pb: 2 }}>
                                                    <Button
                                                        fullWidth
                                                        variant={isPrimary ? "contained" : "outlined"}
                                                        startIcon={
                                                            exporting === report.id ? (
                                                                <CircularProgress size={16} />
                                                            ) : (
                                                                <FileDownloadOutlinedIcon />
                                                            )
                                                        }
                                                        disabled={Boolean(exporting)}
                                                        onClick={() => handleExport(report.id)}
                                                    >
                                                        Export CSV
                                                    </Button>
                                                </CardActions>
                                            </Card>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </Box>

                        {dashboard.enrollmentByClass?.length ? (
                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                    Enrolment preview (by class & sex)
                                </Typography>
                                <Box sx={{ overflowX: "auto" }}>
                                    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                                        <Box component="thead">
                                            <Box component="tr">
                                                {["Class", "Stream", "Male", "Female", "Total", "SEN"].map((h) => (
                                                    <Box
                                                        component="th"
                                                        key={h}
                                                        sx={{ textAlign: "left", py: 1, pr: 2, borderBottom: 1, borderColor: "divider" }}
                                                    >
                                                        {h}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                        <Box component="tbody">
                                            {dashboard.enrollmentByClass.slice(0, 12).map((row) => (
                                                <Box component="tr" key={`${row.classGrade}-${row.stream}`}>
                                                    <Box component="td" sx={{ py: 0.75, pr: 2 }}>
                                                        {row.classGrade}
                                                    </Box>
                                                    <Box component="td" sx={{ py: 0.75, pr: 2 }}>
                                                        {row.stream || "—"}
                                                    </Box>
                                                    <Box component="td" sx={{ py: 0.75, pr: 2 }}>
                                                        {row.male}
                                                    </Box>
                                                    <Box component="td" sx={{ py: 0.75, pr: 2 }}>
                                                        {row.female}
                                                    </Box>
                                                    <Box component="td" sx={{ py: 0.75, pr: 2 }}>
                                                        {row.total}
                                                    </Box>
                                                    <Box component="td" sx={{ py: 0.75, pr: 2 }}>
                                                        {row.senTotal}
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                </Box>
                            </Paper>
                        ) : null}
                    </>
                ) : (
                    <Alert severity="error">Could not load EMIS data. Check your connection and try again.</Alert>
                )}
            </Stack>

            <Popup showPopup={showPopup} setShowPopup={setShowPopup} title="EMIS reports">
                <Typography>{message}</Typography>
            </Popup>
        </Container>
    );
};

export default EmisReportsDashboard;
